import React, { useState, useEffect, useRef } from "react";
import './alertPopup.scss';
import { connect } from "react-redux";
import { updateShowAPIErrorActionCreator } from '../../components/dataEntryDetails/DataEntryDetailsDucks';
import { logoutUserRequest } from '../../components/login/LoginPageDucks';


interface APIErrorObjectProps {
    id?: number,
    showAlert?: boolean,
    status?: number,
    statusText?: string,
    code?: number,
    message?: string,
    messages?: Array<{code:number, message:string}>,
    responseURL?: string
}

interface APIErrorPopUpProps {
    headerText?: String,
    apiErrorArray?: Array<APIErrorObjectProps>,
    updateShowAPIError?: any,
    logoutUser?: any,
}

/**
 * AB#39577: a 4xx is a permanent rejection (the request itself is wrong), never a transient
 * outage - retrying can't succeed, so "Service is temporarily unavailable... try again later"
 * is actively wrong for one and actively encourages the repeated-retry pattern that made
 * several unrelated 400s look like one recurring outage. Only a 5xx / no-status (network
 * failure) keeps that wording. Falls back to a neutral, non-retry message when a 4xx carries
 * no parseable body at all (e.g. a bare BadHttpRequestException, AB#39576).
 *
 * Review round 2 (Nelson Omuto / Mike Hamilton): `obj.message` comes from the legacy
 * CustomError envelope, whose ~60 call sites mostly pass raw `ex.Message` straight through
 * (SQL text, null-ref detail, etc.) - security.md/hipaa-compliance.md bar echoing that.
 * `obj.messages[]` is built exclusively by the API's typed multi-error collector, which never
 * carries exception text, so only that array is safe to render verbatim here.
 */
function getHeadlineMessage(obj: APIErrorObjectProps): string {
    const isClientError = obj.status != null && obj.status >= 400 && obj.status < 500;
    if (!isClientError) {
        return "Service is temporarily unavailable. Please try again later.";
    }
    return obj.messages?.[0]?.message || "That request was rejected.";
}

function APIErrorPopUp(props: APIErrorPopUpProps) {
    const [currentErrorObj, setCurrentErrorObj] = useState<APIErrorObjectProps[]>([])
    const forceLogoutCalled = useRef(false);
    useEffect(() => {
        const tempArr: any = [];
        if (props.apiErrorArray) {
            for (let i = 0; i < props?.apiErrorArray?.length; i++) {
                if (props.apiErrorArray[i].showAlert) {
                    tempArr.push(props.apiErrorArray[i])
                }
            }
        }
        setCurrentErrorObj(tempArr);

        // Detect "UserId {number} not found" and force logout once
        try {
            const re = /UserId\s*(\d+)\s*not found/i;
            for (let i = 0; i < tempArr.length; i++) {
                const obj = tempArr[i];
                if (!obj) continue;
                // check primary message
                if (obj.message && re.test(obj.message)) {
                    const match = obj.message.match(re);
                    if (match && !forceLogoutCalled.current) {
                        forceLogoutCalled.current = true;
                        props.logoutUser && props.logoutUser({ userId: parseInt(match[1], 10), redirect: () => { window.location.replace('/') ; } });
                        break;
                    }
                }
                // check multiple messages
                if (obj.messages && obj.messages.length) {
                    for (let j = 0; j < obj.messages.length; j++) {
                        const m = obj.messages[j];
                        if (m && m.message && re.test(m.message)) {
                            const match = m.message.match(re);
                            if (match && !forceLogoutCalled.current) {
                                forceLogoutCalled.current = true;
                                props.logoutUser && props.logoutUser({ userId: parseInt(match[1], 10), redirect: () => { window.location.replace('/'); } });
                                break;
                            }
                        }
                    }
                }
            }
        } catch (e) {
            // swallow errors - detection shouldn't break popup rendering
        }
    }, [props.apiErrorArray])

    if (!forceLogoutCalled.current && currentErrorObj.length) {
        return (
            <React.Fragment>
                        {
                            currentErrorObj.map((obj, key) =>
                                (obj?.showAlert)
                                    ? (<React.Fragment>
                                <div className="error-backdrop" onClick={() => props.updateShowAPIError(obj.id)}>
                                        <div className="api-error-popup-main" style={{marginLeft: `${obj.id}%`}}>
                                            <div className="alert alert-danger alert-dismissible fade show api-error-popup" role="alert" style={{ backgroundColor: '#FAEEF0', paddingRight: '17px', marginTop: `${obj.id}%` }}>
                                                <div className="alert-icon"></div>
                                                <h5 className="alert-heading">Error occurred
                                                <span className="close-icon alert-close-icon" onClick={() => props.updateShowAPIError(obj.id)}></span>
                                                </h5>

                                                <p className="mb-0 pb-2" style={{ fontSize: '16px' }}>
                                                    {getHeadlineMessage(obj)}
                                            </p>
                                                <p className="mb-0">
                                                    {obj.responseURL}
                                                </p>
                                                <p className="mb-0">
                                                    HTTP Code: {obj.status} - {obj.statusText}
                                                </p>
                                                {obj.message &&
                                                    <p className="mb-0">
                                                        API Error Code: {obj.code} - {obj.message}
                                                    </p>
                                                }
                                                {obj.messages &&
                                                    obj.messages.map(m => 
                                                        <p className="mb-0">
                                                            API Error Code: {m.code} - {m.message}
                                                        </p>
                                                    )
                                                }

                                            </div>
                                        </div>
                                        </div>
                                    </React.Fragment>
                                    ) : null
                            )
                        }
            </React.Fragment>

        );
    }
    else {
        return null
    }

}

function mapStateToProps(state: any) {
    return {
        apiErrorArray: state.dataEntryState?.apiErrorArray
    };
}

function mapDispatchToProps(dispatch: Function) {
    return {
        updateShowAPIError: (id: number) => dispatch(updateShowAPIErrorActionCreator(id)),
        logoutUser: (action: any) => dispatch(logoutUserRequest(action)),
    }
}

export default connect<{}, {}, APIErrorPopUpProps>(mapStateToProps, mapDispatchToProps)(
    APIErrorPopUp
);